/** Share links.
 *
 * The public read is deliberately **not** routed through the authenticated client: that
 * client attaches a bearer token, and a share page is opened by people who have none. It
 * uses a plain fetch so the request is genuinely anonymous.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { api, ApiError } from '@/lib/api/client'

export interface ShareLink {
  id: string
  token: string
  kind: 'project' | 'status'
  projectId: string | null
  label: string
  createdBy: string
  createdDate: string
  expiresAt: string | null
  revokedAt: string | null
  viewCount: number
  lastViewedAt: string | null
  isActive: boolean
  rowVersion: number
}

/** A project reduced to what a share link may show.
 *
 * Mirrors `ShareViewService.project` on the API, whose docstring is the authority on why
 * each field is here and why others are not. Nothing on this page is behind a sign-in, so
 * the shape is the contract for what leaves the building.
 */
export interface SharedProject {
  kind: 'project'
  label: string
  name: string
  description: string
  stage: string
  status: string
  ragStatus: string
  percentComplete: number
  teamLabel: string
  teamShortLabel: string
  owner: string
  startDate: string | null
  targetDate: string | null
  actualEndDate: string | null
  updatedAt: string
  schedule: {
    daysElapsed: number | null
    daysRemaining: number | null
    overdueBy: number | null
    durationDays: number | null
    timeUsedPercent: number | null
  }
  milestones: {
    title: string
    dueDate: string | null
    isComplete: boolean
    isOverdue: boolean
  }[]
  delivery: {
    /** Open work, then what finished in the last `recentlyDoneDays` days. */
    items: SharedWorkItem[]
    itemsOmitted: number
    recentlyDoneDays: number
    total: number
    done: number
    inProgress: number
    blocked: number
    pointsTotal: number | null
    pointsDone: number | null
    currentSprint: {
      name: string
      goal: string
      startDate: string
      endDate: string
      daysLeft: number
      items: number
      itemsDone: number
    } | null
  }
  trackers: {
    title: string
    done: number | null
    total: number | null
    percent: number | null
    syncedHoursAgo: number | null
  }[]
  dependencies: {
    waitingOn: SharedDependency[]
    blocking: SharedDependency[]
  }
  team: { name: string; role: string; isOwner: boolean }[]
  documents: {
    slot: string
    label: string
    present: boolean
    updatedAt: string | null
    approved: boolean
    signedBy: string | null
    signedAt: string | null
  }[]
  closure: { closedOn: string | null; summary: string | null } | null
}

export interface SharedDependency {
  name: string
  status: string
  ragStatus: string
  percentComplete: number
  targetDate: string | null
}

export interface SharedStatus {
  kind: 'status'
  label: string
  generatedAt: string
  totals: {
    projects: number
    active: number
    complete: number
    atRisk: number
    overdue: number
  }
  byRag: Record<string, number>
  /** Labelled and sorted by the API, so a team rename needs no client change. */
  byTeam: { team: string; label: string; count: number }[]
  projects: {
    name: string
    stage: string
    status: string
    ragStatus: string
    percentComplete: number
    owner: string
    teamLabel: string
    targetDate: string | null
    overdue: boolean
    overdueBy: number | null
    nextMilestone: { title: string; dueDate: string; isOverdue: boolean } | null
  }[]
}

const key = ['shares'] as const

/** The absolute URL to hand to someone. */
export function shareUrl(token: string): string {
  return `${window.location.origin}/s/${token}`
}

export function useShares() {
  return useQuery({
    queryKey: key,
    queryFn: async () => {
      const { data } = await api.GET('/api/v1/shares', {})
      return (data as unknown as { shares: ShareLink[] }).shares
    },
  })
}

export function useCreateShare() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: async (body: {
      kind: 'project' | 'status'
      project_id?: string | null
      label?: string
      ttl_days?: number
    }) => {
      const { data } = await api.POST('/api/v1/shares', { body: body as never })
      return data as unknown as ShareLink
    },
    onSuccess: () => void client.invalidateQueries({ queryKey: key }),
  })
}

export function useRevokeShare() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, reason }: { id: string; reason: string }) => {
      const { data } = await api.POST('/api/v1/shares/{share_id}/revoke', {
        params: { path: { share_id: id } },
        body: { reason } as never,
      })
      return data as unknown as ShareLink
    },
    onSuccess: () => void client.invalidateQueries({ queryKey: key }),
  })
}

/**
 * Read a shared page anonymously.
 *
 * Retries are off: the only failure this endpoint produces is "the link is no longer
 * available", and repeating an unknown token three times just writes three denials into
 * the audit trail.
 */
export function useSharedPage(token: string | undefined) {
  return useQuery({
    queryKey: ['public-share', token],
    enabled: Boolean(token),
    retry: false,
    queryFn: async () => {
      const origin = import.meta.env.VITE_API_ORIGIN ?? ''
      const response = await fetch(`${origin}/api/v1/public/shares/${token ?? ''}`)
      if (!response.ok) {
        let body: { message?: string; code?: string; correlationId?: string | null } | null = null
        try {
          body = (await response.json()) as typeof body
        } catch {
          body = null
        }
        throw new ApiError(response.status, body)
      }
      return (await response.json()) as SharedProject | SharedStatus
    },
  })
}

export interface SharedWorkItem {
  title: string
  description: string
  status: 'todo' | 'in_progress' | 'blocked' | 'done'
  assignee: string | null
  completedOn: string | null
}
